/**
 * Split a carrier's reply from everything their mail client stapled underneath.
 *
 * Josh, 2026-09-08, on reading his first stored reply: "what is the quote
 * trimming?" — the thread was rendering 54 lines to tell him a desk had said
 * "here you go". That message measured 1,648 characters; 11 of them were the
 * reply. The rest was his own letter quoted back, a signature, and Proofpoint's
 * spam-scanner note carrying a 444-character URL.
 *
 * ⚠️ THIS SPLITS, IT NEVER DELETES. Both halves come back and the caller is
 * expected to keep the second one reachable. Carriers answer inside the quote
 * more often than is comfortable — a desk that replies inline under each policy
 * number puts the only useful content below the marker — and a trim that
 * silently hides the answer is worse than no trim at all.
 *
 * ⚠️ AND IT IS FOR DISPLAY, NOT FOR STORAGE. body_text as stored is what
 * looksAutomated() reads to recognise an out-of-office and what
 * applyReplyToStatus acts on. Trimming at the API boundary would quietly change
 * which replies move a status, so the raw message stays the record and this runs
 * at the point of rendering.
 *
 * ⚠️ THE EARLIEST MARKER WINS, NOT THE FIRST ONE FOUND. A top-posted reply can
 * carry its signature above the quote ("Thanks!" / "--" / "Jane" / "On Mon…"),
 * so scanning stops being about order and becomes about the minimum index.
 */
/**
 * The line index where the sender's own words stop, or -1 if they never do.
 *
 * Every marker here is one a mail client writes, never one a person types. That
 * is the whole basis for cutting on it.
 */
function boundary(lines) {
    let cut = -1;
    const mark = (i) => {
        if (i >= 0 && (cut < 0 || i < cut))
            cut = i;
    };
    for (let i = 0; i < lines.length; i++) {
        const line = (lines[i] ?? '').trim();
        if (!line)
            continue;
        /*
          "On Tue, Sep 8, 2026 at 10:54 AM AASOUT <AASOUT@…> wrote:" — Gmail, Apple
          Mail and most phones. It wraps when the sender's name is long, so a line
          ending in "wrote:" is only a boundary when an "On …" line opened it, and
          the cut belongs at that opening line rather than where it happens to end.
        */
        if (/\bwrote:$/i.test(line)) {
            for (let back = i; back >= Math.max(0, i - 3); back--) {
                if (/^on\b/i.test((lines[back] ?? '').trim())) {
                    mark(back);
                    break;
                }
            }
        }
        // Outlook, forwarding or replying with history.
        if (/^-{2,}\s*original message\s*-{2,}$/i.test(line))
            mark(i);
        // Outlook's own rule above a quoted block, and a scanner's banner rule.
        if (/^_{10,}$/.test(line) || /^-{20,}$/.test(line))
            mark(i);
        // RFC 3676 signature delimiter. Gmail drops the trailing space.
        if (/^--$/.test(line))
            mark(i);
        // The first quoted line, whatever came before it.
        if (line.startsWith('>'))
            mark(i);
        // Proofpoint, which sits in front of this tenant's inbound mail.
        if (/scanned for spam and viruses/i.test(line))
            mark(i);
        /*
          An Outlook forward header. "From:" alone is not enough — it has to be the
          top of a header block, or a carrier opening a sentence with "From: our
          side, everything is approved" loses their own message.
        */
        if (/^from:\s*\S/i.test(line)) {
            const near = lines.slice(i + 1, i + 5).join('\n');
            if (/^\s*(sent|to|subject):/im.test(near))
                mark(i);
        }
    }
    return cut;
}
/** Split a plain-text email body into what was written and what was carried. */
export function splitQuoted(text) {
    const body = String(text ?? '').replace(/\r\n/g, '\n');
    const lines = body.split('\n');
    const cut = boundary(lines);
    if (cut < 0)
        return { reply: body.trim(), quoted: '' };
    const reply = lines.slice(0, cut).join('\n').trim();
    const quoted = lines.slice(cut).join('\n').trim();
    /*
      ⚠️ NOTHING ABOVE THE MARKER MEANS IT WAS NOT A BOUNDARY. A reply that opens
      on a quoted line, or is nothing but a forwarded block, would otherwise
      render as an empty message with everything hidden behind a toggle — the
      thread would look like the carrier sent blank mail. Show it whole instead.
    */
    if (!reply)
        return { reply: body.trim(), quoted: '' };
    return { reply, quoted };
}
